import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
    Search,
    Filter,
    Plus,
    Download,
    ChevronUp,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    CheckCircle,
    AlertTriangle,
    XCircle,
    MoreHorizontal,
    Eye,
    FileText,
    Trash2,
    X,
    Loader2
} from 'lucide-react';
import { useLoans, useLoanMutations, useCovenants } from '../hooks';
import { formatCurrency, getStatusColor } from '../utils/helper';
import AddLoanModal from '../components/Loans/AddLoanModal';
import { useAuth } from '../context/AuthContext';
import { PermissionGate, PermissionButton } from '../components/common/PermissionGate';
import { useTranslation } from '../context/LanguageContext';
import { downloadLoanReport } from '../utils/pdfExport';
import { useActivityLog, LOG_TYPES, LOG_CATEGORIES } from '../context/ActivityLogContext';


const StatusBadge = ({ status, t }) => {
    const normalizedStatus = status === 'compliant' ? 'ok' : status === 'at_risk' ? 'warning' : status === 'breach' ? 'critical' : status;
    const colors = getStatusColor(normalizedStatus);
    const icons = {
        ok: <CheckCircle size={14} />,
        warning: <AlertTriangle size={14} />,
        critical: <XCircle size={14} />,
        active: <CheckCircle size={14} />,
        archived: <CheckCircle size={14} />
    };
    const labels = {
        ok: t('dashboard.compliant'),
        warning: t('dashboard.atRisk'),
        critical: t('dashboard.breach'),
        active: 'Active',
        archived: 'Archived'
    };

    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${colors.bg} ${colors.text} ${colors.border}`}>
      {icons[normalizedStatus] || <CheckCircle size={14} />}
            {labels[normalizedStatus] || status}
    </span>
    );
};


const ActionMenu = ({ loanId, onClose, buttonRef, hasPermission, t, onExport, onDelete }) => {
    const getPosition = () => {
        if (!buttonRef) return { top: 0, right: 40 };

        const rect = buttonRef.getBoundingClientRect();
        const dropdownHeight = 150;
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
            return {
                bottom: window.innerHeight - rect.top + 4,
                right: window.innerWidth - rect.right,
            };
        }

        return {
            top: rect.bottom + 4,
            right: window.innerWidth - rect.right,
        };
    };

    return (
        <div
            className="fixed z-50 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1"
            style={getPosition()}
        >
            <Link
                to={`/loans/${loanId}`}
                onClick={onClose}
                className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
                <Eye size={16} />
                {t('loans.viewDetails')}
            </Link>
            {hasPermission('export_reports') && (
                <button
                    onClick={() => {
                        onExport(loanId);
                        onClose();
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                >
                    <FileText size={16} />
                    {t('loans.exportReport')}
                </button>
            )}
            {hasPermission('delete_loan') && (
                <>
                    <hr className="my-1 border-slate-100" />
                    <button
                        onClick={() => {
                            onDelete(loanId);
                            onClose();
                        }}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                        <Trash2 size={16} />
                        {t('loans.deleteLoan')}
                    </button>
                </>
            )}
        </div>
    );
};

function Loans() {
    const { hasPermission, user } = useAuth();
    const { t } = useTranslation();
    const { addLog } = useActivityLog();


    const userContext = user ? { role: user.role, userId: user.id } : null;


    const { loans: dbLoans, loading, error, refetch } = useLoans({}, userContext);
    const { createLoan, deleteLoan } = useLoanMutations();


    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [loanToDelete, setLoanToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);


    const loans = useMemo(() => {
        return dbLoans.map(loan => ({
            id: loan.id,
            borrower: loan.borrower,
            leadArranger: loan.lead_arranger,
            bank: loan.bank,
            amount: loan.amount,
            currency: loan.currency,
            startDate: loan.start_date,
            maturityDate: loan.maturity_date,
            nextReportingDate: loan.next_reporting_date,
            sector: loan.sector,
            country: loan.country,
            status: loan.status,
            covenants: loan.covenants || [],
            covenantCount: loan.covenant_count || 0,
            issuesCount: loan.issues_count || 0
        }));
    }, [dbLoans]);


    const [searchQuery, setSearchQuery] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [showAddLoanModal, setShowAddLoanModal] = useState(false);
    const [filters, setFilters] = useState({
        status: 'all',
        sector: 'all',
        country: 'all'
    });
    const [sortConfig, setSortConfig] = useState({ key: 'borrower', direction: 'asc' });
    const [currentPage, setCurrentPage] = useState(1);
    const [activeMenu, setActiveMenu] = useState(null);
    const itemsPerPage = 10;


    const handleAddLoan = async (newLoan) => {
        const result = await createLoan({
            reference: newLoan.reference || `LN-${Date.now()}`,
            borrower: newLoan.borrower,
            lead_arranger: newLoan.leadArranger,
            bank: newLoan.bank || 'BNP Paribas',
            amount: parseFloat(newLoan.amount),
            currency: newLoan.currency || 'EUR',
            start_date: newLoan.startDate,
            maturity_date: newLoan.maturityDate,
            next_reporting_date: newLoan.nextReportingDate,
            sector: newLoan.sector,
            country: newLoan.country,
            status: 'ok'
        }, user?.id || 1);

        if (result.success) {

            addLog({
                type: LOG_TYPES.ACTION,
                category: LOG_CATEGORIES.LOAN,
                action: 'create_loan',
                message: `Nouveau prêt créé: ${newLoan.borrower}`,
                details: {
                    loanId: result.data?.id,
                    borrower: newLoan.borrower,
                    amount: newLoan.amount,
                    sector: newLoan.sector
                }
            });
            refetch();
        }
    };


    const sectors = [...new Set(loans.map(l => l.sector))];
    const countries = [...new Set(loans.map(l => l.country))];


    const filteredLoans = useMemo(() => {
        let result = [...loans];


        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            result = result.filter(loan =>
                loan.borrower.toLowerCase().includes(query) ||
                loan.leadArranger.toLowerCase().includes(query) ||
                loan.sector.toLowerCase().includes(query)
            );
        }


        if (filters.status !== 'all') {
            result = result.filter(loan => loan.status === filters.status);
        }


        if (filters.sector !== 'all') {
            result = result.filter(loan => loan.sector === filters.sector);
        }


        if (filters.country !== 'all') {
            result = result.filter(loan => loan.country === filters.country);
        }


        result.sort((a, b) => {
            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];


            if (sortConfig.key === 'covenantCount') {
                aVal = a.covenants.length;
                bVal = b.covenants.length;
            }


            if (sortConfig.key === 'status') {
                const order = { critical: 0, warning: 1, ok: 2 };
                aVal = order[aVal];
                bVal = order[bVal];
            }

            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });

        return result;
    }, [loans, searchQuery, filters, sortConfig]);


    const totalPages = Math.ceil(filteredLoans.length / itemsPerPage);
    const paginatedLoans = filteredLoans.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );


    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const resetFilters = () => {
        setFilters({ status: 'all', sector: 'all', country: 'all' });
        setSearchQuery('');
    };

    const activeFiltersCount = Object.values(filters).filter(v => v !== 'all').length;


    const handleExportReport = (loanId) => {
        const loan = loans.find(l => l.id === loanId);
        if (loan) {
            downloadLoanReport(loan, [], []);

            addLog({
                type: LOG_TYPES.ACTION,
                category: LOG_CATEGORIES.REPORT,
                action: 'export_report',
                message: `Rapport exporté pour ${loan.borrower}`,
                details: { loanId: loan.id, borrower: loan.borrower }
            });
        }
    };


    const handleDeleteClick = (loanId) => {
        const loan = loans.find(l => l.id === loanId);
        setLoanToDelete(loan);
        setShowDeleteModal(true);
    };


    const handleConfirmDelete = async () => {
        if (!loanToDelete) return;

        setIsDeleting(true);
        try {
            const result = await deleteLoan(loanToDelete.id);
            if (result.success) {

                addLog({
                    type: LOG_TYPES.ACTION,
                    category: LOG_CATEGORIES.LOAN,
                    action: 'delete_loan',
                    message: `Prêt supprimé: ${loanToDelete.borrower}`,
                    details: {
                        loanId: loanToDelete.id,
                        borrower: loanToDelete.borrower,
                        amount: loanToDelete.amount
                    }
                });
                await refetch();
                setShowDeleteModal(false);
                setLoanToDelete(null);
            }
        } catch (err) {
            console.error('Error deleting loan:', err);
        }
        setIsDeleting(false);
    };


    const SortIndicator = ({ columnKey }) => {
        if (sortConfig.key !== columnKey) {
            return <ChevronUp size={14} className="text-slate-300" />;
        }
        return sortConfig.direction === 'asc'
            ? <ChevronUp size={14} className="text-blue-600" />
            : <ChevronDown size={14} className="text-blue-600" />;
    };


    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
        );
    }

    return (
        <div className="space-y-4 sm:space-y-6">
            {}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-slate-800">{t('loans.title')}</h2>
                    <p className="text-sm sm:text-base text-slate-500">
                        {filteredLoans.length} {filteredLoans.length !== 1 ? t('nav.loans').toLowerCase() : t('nav.loans').toLowerCase().slice(0, -1)}
                        {activeFiltersCount > 0 && ` (${t('common.filter').toLowerCase()})`}
                    </p>
                </div>
                <div className="flex gap-2 sm:gap-3 w-full sm:w-auto">
                    <PermissionGate permission="export_reports">
                        <button className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 sm:px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 text-slate-700 text-sm sm:text-base">
                            <Download size={18} />
                            <span className="hidden sm:inline">{t('common.export')}</span>
                        </button>
                    </PermissionGate>
                    <PermissionGate permission="create_loan">
                        <button
                            onClick={() => setShowAddLoanModal(true)}
                            className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm sm:text-base"
                        >
                            <Plus size={18} />
                            <span className="hidden sm:inline">{t('loans.addLoan')}</span>
                            <span className="sm:hidden">{t('common.add')}</span>
                        </button>
                    </PermissionGate>
                </div>
            </div>

            {}
            <AddLoanModal
                isOpen={showAddLoanModal}
                onClose={() => setShowAddLoanModal(false)}
                onSubmit={handleAddLoan}
            />

            {}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3 sm:p-4">
                <div className="flex gap-2 sm:gap-4">
                    {}
                    <div className="relative flex-1">
                        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            type="text"
                            placeholder={`${t('common.search')}...`}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                        />
                    </div>

                    {}
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 border rounded-lg transition-colors text-sm sm:text-base ${
                            showFilters || activeFiltersCount > 0
                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                        }`}
                    >
                        <Filter size={18} />
                        <span className="hidden sm:inline">{t('common.filter')}</span>
                        {activeFiltersCount > 0 && (
                            <span className="bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                {activeFiltersCount}
              </span>
                        )}
                    </button>
                </div>

                {}
                {showFilters && (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:items-end">
                            {}
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-slate-700 mb-1">
                                    {t('loans.status')}
                                </label>
                                <select
                                    value={filters.status}
                                    onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                                >
                                    <option value="all">{t('loans.allStatuses')}</option>
                                    <option value="ok">{t('dashboard.compliant')}</option>
                                    <option value="warning">{t('dashboard.atRisk')}</option>
                                    <option value="critical">{t('dashboard.breach')}</option>
                                </select>
                            </div>

                            {}
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-slate-700 mb-1">
                                    {t('loans.sector')}
                                </label>
                                <select
                                    value={filters.sector}
                                    onChange={(e) => setFilters(prev => ({ ...prev, sector: e.target.value }))}
                                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                                >
                                    <option value="all">{t('loans.allSectors')}</option>
                                    {sectors.map(sector => (
                                        <option key={sector} value={sector}>{sector}</option>
                                    ))}
                                </select>
                            </div>

                            {}
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-slate-700 mb-1">
                                    {t('loans.country')}
                                </label>
                                <select
                                    value={filters.country}
                                    onChange={(e) => setFilters(prev => ({ ...prev, country: e.target.value }))}
                                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                                >
                                    <option value="all">{t('loans.allCountries')}</option>
                                    {countries.map(country => (
                                        <option key={country} value={country}>{country}</option>
                                    ))}
                                </select>
                            </div>

                            {}
                            <button
                                onClick={resetFilters}
                                className="flex items-center justify-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-800"
                            >
                                <X size={18} />
                                {t('common.reset')}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {}
            <div className="hidden md:block bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                <table className="w-full min-w-[800px]">
                    <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                        <th
                            onClick={() => handleSort('borrower')}
                            className="text-left px-6 py-4 text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-100"
                        >
                            <div className="flex items-center gap-1">
                                {t('loans.borrower')}
                                <SortIndicator columnKey="borrower" />
                            </div>
                        </th>
                        <th
                            onClick={() => handleSort('amount')}
                            className="text-left px-6 py-4 text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-100"
                        >
                            <div className="flex items-center gap-1">
                                {t('loans.amount')}
                                <SortIndicator columnKey="amount" />
                            </div>
                        </th>
                        <th
                            onClick={() => handleSort('sector')}
                            className="text-left px-6 py-4 text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-100"
                        >
                            <div className="flex items-center gap-1">
                                {t('loans.sector')}
                                <SortIndicator columnKey="sector" />
                            </div>
                        </th>
                        <th
                            onClick={() => handleSort('maturityDate')}
                            className="text-left px-6 py-4 text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-100"
                        >
                            <div className="flex items-center gap-1">
                                {t('loans.maturityDate')}
                                <SortIndicator columnKey="maturityDate" />
                            </div>
                        </th>
                        <th
                            onClick={() => handleSort('covenantCount')}
                            className="text-left px-6 py-4 text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-100"
                        >
                            <div className="flex items-center gap-1">
                                {t('loans.covenants')}
                                <SortIndicator columnKey="covenantCount" />
                            </div>
                        </th>
                        <th
                            onClick={() => handleSort('status')}
                            className="text-left px-6 py-4 text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-100"
                        >
                            <div className="flex items-center gap-1">
                                {t('loans.status')}
                                <SortIndicator columnKey="status" />
                            </div>
                        </th>
                        <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">
                            {t('common.actions')}
                        </th>
                    </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                    {paginatedLoans.map((loan) => (
                        <tr key={loan.id} className="hover:bg-slate-50 transition-colors">
                            <td className="px-6 py-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold">
                                        {(loan.borrower || loan.borrower_name || loan.facility_name || 'L')?.charAt(0)}
                                    </div>
                                    <div>
                                        <Link
                                            to={`/loans/${loan.id}`}
                                            className="font-medium text-slate-800 hover:text-blue-600"
                                        >
                                            {loan.borrower || loan.borrower_name || loan.facility_name || 'Unknown'}
                                        </Link>
                                        <p className="text-sm text-slate-500">{loan.leadArranger || loan.lead_arranger || '-'}</p>
                                    </div>
                                </div>
                            </td>
                            <td className="px-6 py-4">
                  <span className="font-semibold text-slate-800">
                    {formatCurrency(loan.amount)}
                  </span>
                            </td>
                            <td className="px-6 py-4">
                                <span className="text-slate-600">{loan.sector}</span>
                            </td>
                            <td className="px-6 py-4">
                  <span className="text-slate-600">
                    {new Date(loan.maturityDate).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                    })}
                  </span>
                            </td>
                            <td className="px-6 py-4">
                                <div className="flex items-center gap-2">
                                    <span className="font-medium text-slate-800">{loan.covenants.length}</span>
                                    {loan.covenants.filter(c => c.status !== 'ok').length > 0 && (
                                        <span className="text-xs text-red-600">
                        ({loan.covenants.filter(c => c.status !== 'ok').length} {t('loans.issues')})
                      </span>
                                    )}
                                </div>
                            </td>
                            <td className="px-6 py-4">
                                <StatusBadge status={loan.status} t={t} />
                            </td>
                            <td className="px-6 py-4">
                                <button
                                    data-loan-id={loan.id}
                                    onClick={() => setActiveMenu(activeMenu === loan.id ? null : loan.id)}
                                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                                >
                                    <MoreHorizontal size={18} className="text-slate-500" />
                                </button>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>

                {}
                {filteredLoans.length === 0 && (
                    <div className="text-center py-12">
                        <FileText size={48} className="mx-auto text-slate-300 mb-4" />
                        <h3 className="text-lg font-medium text-slate-800 mb-1">{t('loans.noLoansFound')}</h3>
                        <p className="text-slate-500">{t('loans.adjustFilters')}</p>
                    </div>
                )}

                {}
                {totalPages > 1 && (
                    <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
                        <p className="text-sm text-slate-500">
                            {t('loans.showing')} {((currentPage - 1) * itemsPerPage) + 1} {t('loans.to')} {Math.min(currentPage * itemsPerPage, filteredLoans.length)} {t('loans.of')} {filteredLoans.length} {t('nav.loans').toLowerCase()}
                        </p>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                                className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <ChevronLeft size={18} />
                            </button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                <button
                                    key={page}
                                    onClick={() => setCurrentPage(page)}
                                    className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                                        currentPage === page
                                            ? 'bg-blue-600 text-white'
                                            : 'hover:bg-slate-100 text-slate-700'
                                    }`}
                                >
                                    {page}
                                </button>
                            ))}
                            <button
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages}
                                className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <ChevronRight size={18} />
                            </button>
                        </div>
                    </div>
                )}
                </div>
            </div>

            {}
            {activeMenu && (
                <>
                    <div
                        className="fixed inset-0 z-40"
                        onClick={() => setActiveMenu(null)}
                    />
                    <ActionMenu
                        loanId={activeMenu}
                        onClose={() => setActiveMenu(null)}
                        buttonRef={document.querySelector(`[data-loan-id="${activeMenu}"]`)}
                        hasPermission={hasPermission}
                        t={t}
                        onExport={handleExportReport}
                        onDelete={handleDeleteClick}
                    />
                </>
            )}

            {}
            <div className="md:hidden space-y-3">
                {paginatedLoans.length > 0 ? (
                    paginatedLoans.map((loan) => (
                        <div key={loan.id} className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold shrink-0">
                                        {(loan.borrower || loan.borrower_name || loan.facility_name || 'L')?.charAt(0)}
                                    </div>
                                    <div className="min-w-0">
                                        <Link
                                            to={`/loans/${loan.id}`}
                                            className="font-medium text-slate-800 hover:text-blue-600 block truncate"
                                        >
                                            {loan.borrower || loan.borrower_name || loan.facility_name || 'Unknown'}
                                        </Link>
                                        <p className="text-sm text-slate-500 truncate">{loan.leadArranger || loan.lead_arranger || '-'}</p>
                                    </div>
                                </div>
                                <StatusBadge status={loan.status} t={t} />
                            </div>

                            <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                                <div>
                                    <p className="text-slate-500">{t('loans.amount')}</p>
                                    <p className="font-semibold text-slate-800">{formatCurrency(loan.amount)}</p>
                                </div>
                                <div>
                                    <p className="text-slate-500">{t('loans.sector')}</p>
                                    <p className="text-slate-700">{loan.sector}</p>
                                </div>
                                <div>
                                    <p className="text-slate-500">{t('loans.maturityDate')}</p>
                                    <p className="text-slate-700">
                                        {new Date(loan.maturityDate).toLocaleDateString('en-GB', {
                                            day: 'numeric',
                                            month: 'short',
                                            year: 'numeric'
                                        })}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-slate-500">{t('loans.covenants')}</p>
                                    <p className="text-slate-700">
                                        {loan.covenants.length}
                                        {loan.covenants.filter(c => c.status !== 'ok').length > 0 && (
                                            <span className="text-xs text-red-600 ml-1">
                                                ({loan.covenants.filter(c => c.status !== 'ok').length} {t('loans.issues')})
                                            </span>
                                        )}
                                    </p>
                                </div>
                            </div>

                            <div className="flex gap-2 pt-3 border-t border-slate-100">
                                <Link
                                    to={`/loans/${loan.id}`}
                                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                                >
                                    <Eye size={16} />
                                    {t('loans.viewDetails')}
                                </Link>
                                <button className="px-3 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50">
                                    <MoreHorizontal size={18} />
                                </button>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 text-center py-12 px-4">
                        <FileText size={40} className="mx-auto text-slate-300 mb-3" />
                        <h3 className="text-base font-medium text-slate-800 mb-1">{t('loans.noLoansFound')}</h3>
                        <p className="text-sm text-slate-500">{t('loans.adjustFilters')}</p>
                    </div>
                )}

                {}
                {totalPages > 1 && (
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 px-4 py-3 flex flex-col items-center gap-3">
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                                className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <ChevronLeft size={18} />
                            </button>
                            <span className="px-4 py-2 text-sm font-medium text-slate-700">
                                {t('loans.page')} {currentPage} {t('loans.of')} {totalPages}
                            </span>
                            <button
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages}
                                className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <ChevronRight size={18} />
                            </button>
                        </div>
                        <p className="text-xs text-slate-500">
                            {t('loans.showing')} {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, filteredLoans.length)} {t('loans.of')} {filteredLoans.length}
                        </p>
                    </div>
                )}
            </div>

            {}
            {showDeleteModal && loanToDelete && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
                        <div className="p-6">
                            <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mx-auto mb-4">
                                <Trash2 className="w-6 h-6 text-red-600" />
                            </div>
                            <h2 className="text-xl font-bold text-slate-800 text-center mb-2">
                                {t('loans.confirmDeleteTitle')}
                            </h2>
                            <p className="text-slate-600 text-center mb-2">
                                {t('loans.confirmDeleteMessage')}
                            </p>
                            <p className="text-slate-800 font-semibold text-center mb-6">
                                {loanToDelete.borrower}
                            </p>
                            <p className="text-sm text-slate-500 text-center mb-6">
                                {t('loans.confirmDeleteWarning')}
                            </p>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => {
                                        setShowDeleteModal(false);
                                        setLoanToDelete(null);
                                    }}
                                    className="flex-1 py-2.5 px-4 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
                                    disabled={isDeleting}
                                >
                                    {t('common.cancel')}
                                </button>
                                <button
                                    onClick={handleConfirmDelete}
                                    disabled={isDeleting}
                                    className="flex-1 py-2.5 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-red-400 transition-colors flex items-center justify-center gap-2"
                                >
                                    {isDeleting ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            {t('common.deleting')}
                                        </>
                                    ) : (
                                        <>
                                            <Trash2 size={18} />
                                            {t('common.delete')}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Loans;